import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { modeAPI } from '../../services/api';
import './PostJobChoiceModal.css';

const MAX_COMPANIES = 5;

const PostJobChoiceModal = ({ isOpen, onClose, onPostJobDirect, onCreateCompany, onSelectCompany }) => {
  const [mounted, setMounted] = useState(false);
  const [companyProfiles, setCompanyProfiles] = useState([]);
  const [loadingCompanies, setLoadingCompanies] = useState(false);

  const atCompanyLimit = companyProfiles.length >= MAX_COMPANIES;

  useEffect(() => {
    setMounted(true);
    return () => setMounted(false);
  }, []);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      setCompanyProfiles([]);
      setLoadingCompanies(true);
      modeAPI.getCompanyProfiles()
        .then((res) => {
          if (res.success && res.data) setCompanyProfiles(res.data);
        })
        .catch(() => setCompanyProfiles([]))
        .finally(() => setLoadingCompanies(false));
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  if (!isOpen || !mounted) return null;

  const handlePostJobDirect = () => {
    onClose();
    onPostJobDirect?.();
  };

  const handleCreateCompany = () => {
    onClose();
    onCreateCompany?.();
  };

  const handleSelectCompany = (company) => {
    onClose();
    onSelectCompany?.(company);
  };

  const modalContent = (
    <div className="post-job-choice-modal-overlay" onClick={onClose}>
      <div className="post-job-choice-modal" onClick={(e) => e.stopPropagation()}>
        <header className="post-job-choice-modal-header">
          <h2>Post a Job</h2>
          <button type="button" className="post-job-choice-modal-close" onClick={onClose} aria-label="Close">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </header>

        <div className="post-job-choice-modal-body">
          {/* Scrollable company list — fixed height so long lists don't hide actions */}
          <section className="post-job-choice-companies-section">
            <h3 className="post-job-choice-section-title">
              Post as a company
              {companyProfiles.length > 0 && (
                <span className="post-job-choice-section-count">({companyProfiles.length}/{MAX_COMPANIES})</span>
              )}
            </h3>
            {loadingCompanies && companyProfiles.length === 0 ? (
              <div className="post-job-choice-list-wrap post-job-choice-list-loading">
                <span className="post-job-choice-loading-text">Loading your companies…</span>
              </div>
            ) : companyProfiles.length > 0 ? (
              <div className="post-job-choice-list-wrap post-job-choice-list-scroll">
                <ul className="post-job-choice-company-list" role="list">
                  {companyProfiles.map((company) => (
                    <li key={company.id}>
                      <button
                        type="button"
                        className="post-job-choice-company-option"
                        onClick={() => handleSelectCompany(company)}
                      >
                        <div className="post-job-choice-company-option-logo">
                          {company.logo_url ? (
                            <img src={company.logo_url} alt="" />
                          ) : (
                            <span className="post-job-choice-company-option-initial">
                              {(company.name || 'C').charAt(0).toUpperCase()}
                            </span>
                          )}
                        </div>
                        <div className="post-job-choice-company-option-info">
                          <span className="post-job-choice-company-name">{company.name}</span>
                          {company.address && (
                            <span className="post-job-choice-company-option-meta">{company.address}</span>
                          )}
                        </div>
                        <span className="post-job-choice-option-cta">Post as company</span>
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            ) : (
              <div className="post-job-choice-list-wrap post-job-choice-list-empty">
                <span className="post-job-choice-empty-text">No companies yet. Create one below to post as a company.</span>
              </div>
            )}
          </section>

          {/* Always visible: Post directly + Create company */}
          <section className="post-job-choice-actions-section" aria-label="Other options">
            <h3 className="post-job-choice-section-title">Other options</h3>
            <div className="post-job-choice-options">
              <button
                type="button"
                className="post-job-choice-option primary"
                onClick={handlePostJobDirect}
              >
                <div className="post-job-choice-option-icon">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M4.5 16.5c-1.5 1.26-2 5-2 5s3.74-.5 5-2c.71-.84.7-2.13-.09-2.91a2.18 2.18 0 0 0-2.91-.09z" />
                    <path d="m12 15-3-3a22 22 0 0 1 2-3.95A12.88 12.88 0 0 1 22 2c0 2.72-.78 7.5-6 11a22.35 22.35 0 0 1-4 2z" />
                    <path d="M9 12H4s.55-3.03 2-4c1.62-1.08 5 0 5 0" />
                    <path d="M12 15v5s3.03-.55 4-2c1.08-1.62 0-5 0-5" />
                  </svg>
                </div>
                <div className="post-job-choice-option-content">
                  <h4>Post job directly</h4>
                  <p>Post as an individual recruiter. No company setup required.</p>
                </div>
                <span className="post-job-choice-option-cta">Continue</span>
              </button>

              <button
                type="button"
                className={`post-job-choice-option secondary ${atCompanyLimit ? 'disabled' : ''}`}
                onClick={handleCreateCompany}
                disabled={atCompanyLimit}
                title={atCompanyLimit ? 'Maximum 5 companies' : undefined}
              >
                <div className="post-job-choice-option-icon">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
                    <polyline points="9,22 9,12 15,12 15,22" />
                  </svg>
                </div>
                <div className="post-job-choice-option-content">
                  <h4>Create company</h4>
                  <p>
                    {atCompanyLimit
                      ? `You've reached the maximum of ${MAX_COMPANIES} companies. Delete one to add another.`
                      : 'Set up a company profile, then post jobs under your company brand.'}
                  </p>
                </div>
                <span className="post-job-choice-option-cta">{atCompanyLimit ? 'Limit reached' : 'Set up company'}</span>
              </button>
            </div>
          </section>
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default PostJobChoiceModal;
